<template>
  <div class="home mt-2">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
      <div class="d-flex align-items-center flex-wrap">
        <h2 class="mb-0 mr-3">
          Atributos
          <small style="font-size: 13px" v-if="idKeywordSel">servicio {{ idKeywordSel }} / </small>
          <small style="font-size: 13px" v-if="idProductoSel">producto {{ idProductoSel }} / </small>
          <small style="font-size: 13px" v-if="idOperadorSel">operador {{ idOperadorSel }} / </small>
          <small style="font-size: 13px" v-if="idTipoSuscripcionSel">tipo de Suscripción {{ idTipoSuscripcionSel }}</small>
        </h2>
        <span v-if="userRoleLabel" class="badge badge-secondary py-1 px-2" :title="'Atributos visibles para el rol: ' + userRoleLabel">
          <i class="bi bi-shield-lock mr-1"></i>Rol: {{ userRoleLabel }}
        </span>
      </div>
    </div>

    <div class="alert alert-success" role="alert" v-if="success">
      {{ success }}
    </div>
    <div class="alert alert-danger" role="alert" v-if="error">{{ error }}</div>

    <!-- Explicación de los niveles de atributos (Componente encapsulado) -->
    <atributos-niveles-card />
    <div class="alert alert-info py-1 px-2 small mb-1" role="alert">
      <i class="bi bi-info-circle-fill text-info me-1"></i> 
      Coloque el cursor sobre el nombre del atributo para ver la ayuda.
    </div>
    <!-- Barra de búsqueda por nombre de atributo -->
    <div
      class="row mb-2"
      v-if="atributosBusqueda && atributosBusqueda.data && atributosBusqueda.data.length"
    >
      <div class="col-md-5 col-lg-4">
        <div class="input-group input-group-sm">
          <div class="input-group-prepend">
            <span class="input-group-text"><i class="bi bi-search"></i></span>
          </div>
          <input
            type="text"
            class="form-control"
            placeholder="Buscar por nombre del atributo..."
            v-model="filtroAtributoNombre"
          />
          <div class="input-group-append" v-if="filtroAtributoNombre">
            <button
              class="btn btn-outline-secondary"
              type="button"
              @click="filtroAtributoNombre = ''"
              title="Limpiar búsqueda"
            >
              <i class="bi bi-x"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla de atributos -->
    <div
      class="table-responsive"
      v-if="atributosBusqueda && atributosBusqueda.data && atributosBusqueda.data.length"
    >
      <table class="table table-striped table-fixed table-sm">
        <thead>
          <tr>
            <th scope="col" class="col-atributo text-left">Atributo</th>
            <th scope="col" class="col-nivel text-center">Nivel</th>
            <th scope="col" class="col-valor text-left">Valor</th>
            <th scope="col" class="col-acciones text-center">Acciones</th>
          </tr>
        </thead>
      <tbody>
        <tr v-if="atributosFiltrados.length === 0">
          <td colspan="4" class="text-center text-muted py-3">
            <i class="bi bi-info-circle mr-1"></i> No se encontraron atributos que coincidan con "{{ filtroAtributoNombre }}".
          </td>
        </tr>
        <tr
          v-for="atributoItem of atributosFiltrados"
          :key="atributoItem.id_atributo"
          :class="{ 'table-danger fila-vacia': isValorVacio(atributoItem.valor) }"
        >
          <td :title="getTooltipAtributo(atributoItem)" class="text-left">
            <span :class="{ 'font-weight-bold text-danger': isValorVacio(atributoItem.valor) }">
              {{ atributoItem.atributo }}
            </span>
          </td>
          <td class="text-center">
            <span
              class="badge"
              :class="{
                'badge-primary': atributoItem.nivel == 'servicio',
                'badge-info': atributoItem.nivel == 'producto',
                'badge-warning text-dark': atributoItem.nivel == 'operador',
                'badge-secondary': atributoItem.nivel == 'tipo_suscripcion',
                'badge-default-hierarchy bg-white text-dark border border-dark': atributoItem.nivel == 'default',
              }"
              :title="getExplicacionNivel(atributoItem.nivel)"
            >
              {{ atributoItem.nivel }}
            </span>
          </td>
          <td :title="atributoItem.valor" class="text-left">
            <template v-if="atributoItem.atributo == 'billing_sequence_id'">
              <div v-if="!isValorVacio(atributoItem.valor)" class="d-flex align-items-center">
                <a
                  style="cursor: pointer; color: #17a2b8; font-weight: bold;"
                  class="mr-2"
                  title="Ver detalle de la secuencia"
                  @click="getBillingSequencesBusqueda(atributoItem.valor)"
                >
                  <i class="bi bi-info-circle mr-1"></i>{{ atributoItem.valor }}
                </a>
              </div>
              <div v-else>
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm py-0 px-2"
                  style="font-size: 0.8rem;"
                  title="Seleccionar billing sequence"
                  @click="openBillingSequenceSelector(atributoItem, 'create')"
                >
                  <i class="bi bi-list-check mr-1"></i>Seleccionar Billing Sequence
                </button>
              </div>
            </template>
            <span v-else-if="isValorVacio(atributoItem.valor)" class="text-danger font-italic font-weight-bold">
              <i class="bi bi-exclamation-triangle mr-1"></i>(En blanco)
            </span>
            <span v-else>{{ atributoItem.valor }}</span>
          </td>
          <td class="text-center">
            <nobr>
              <!-- Ver Jerarquía -->
              <button
                v-if="atributoItem.nivel != 'default'"
                class="btn btn-info btn-sm mr-1 pt-0 pb-1"
                data-toggle="tooltip"
                data-placement="top"
                title="Jerarquía del atributo"
                @click="showJerarquia(atributoItem)"
              >
                <small class="bi-bar-chart-steps"></small>
              </button>

              <!-- Editar Atributo -->
              <button
                v-if="atributoItem.nivel != 'default'"
                class="btn btn-primary btn-sm mr-1 pt-0 pb-1"
                data-toggle="tooltip"
                data-placement="top"
                title="Editar atributo"
                @click="showEdit(atributoItem)"
              >
                <small class="bi-pencil-fill"></small>
              </button>

              <!-- Eliminar Atributo -->
              <button
                v-if="atributoItem.nivel == nivelpiso && nivelpiso != 'default'"
                class="btn btn-danger btn-sm pt-0 pb-1"
                data-toggle="tooltip"
                data-placement="top"
                title="Eliminar atributo"
                @click="showDelete(atributoItem, atributoItem.nivel)"
              >
                <small class="bi-trash-fill"></small>
              </button>

              <!-- Crear / Editar Default -->
              <button
                v-if="atributoItem.nivel == 'default'"
                class="btn btn-info btn-sm pt-0 pb-1"
                data-toggle="tooltip"
                data-placement="top"
                title="Configurar atributo"
                @click="showCreate(atributoItem)"
              >
                <small v-if="atributoItem.nivel == 'default' && atributoItem.valor && (!idKeywordSel && !idProductoSel && !idOperadorSel && !idTipoSuscripcionSel)" class="bi-pencil-fill"></small>
                <small v-else class="bi-plus"></small>
              </button>
            </nobr>
          </td>
        </tr>
      </tbody>
    </table>
    </div>

    <div
      v-if="
        !(
          atributosBusqueda &&
          atributosBusqueda.data &&
          atributosBusqueda.data.length
        ) && post
      "
    >
      <div class="alert alert-info">
        No se obtuvieron atributos con el criterio seleccionado.
      </div>
    </div>
    <div v-if="error">{{ error.message }}</div>

    <!-- Submodal 1: Jerarquía de Niveles -->
    <atributos-jerarquia-modal
      :show="showJerarquiaModal"
      :atributo="editData"
      :jerarquia="jerarquia"
      :descripcion="getDescripcionAtributo(editData)"
      @close="closeJerarquiaModal"
    />

    <!-- Submodal 2: Vista de Billing Sequence Asociada -->
    <atributos-billing-sequence-modal
      :show="showBillingSequenceModal"
      :billing-sequence="selectedBillingSequence"
      @close="showBillingSequenceModal = false"
    />

    <!-- Submodal 3: Formulario CRUD de Atributo -->
    <atributos-form-modal
      :show="showFormModal"
      :operation="operation"
      :atributo="editData"
      :descripcion="getDescripcionAtributo(editData)"
      :initial-valor="valor"
      :nivel-title="computedNivelTitle"
      :loading="modalLoading"
      :errors="errors"
      @open-billing-selector="openBillingSequenceSelector(editData, operation)"
      @save="onSaveAtributo"
      @delete="deleteAtributo"
      @close="closeFormModal"
    />

    <!-- Submodal 4: Selector de Billing Sequence por Integrador -->
    <atributos-billing-sequence-selector-modal
      :show="showBillingSequenceSelectorModal"
      :atributo="billingSequenceSelectorAtributo || editData"
      :id-integrador="effectiveIntegradorId"
      :integrador-nombre="integradorNombre"
      :current-value="editData ? editData.valor : valor"
      :nivel-title="computedNivelTitle"
      :loading="modalLoading"
      @select="onBillingSequenceSelected"
      @close="showBillingSequenceSelectorModal = false"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { fetchCachedCatalog } from "../../utils/filterCache";
import AtributosNivelesCard from "./atributos/AtributosNivelesCard.vue";
import AtributosJerarquiaModal from "./atributos/AtributosJerarquiaModal.vue";
import AtributosBillingSequenceModal from "./atributos/AtributosBillingSequenceModal.vue";
import AtributosFormModal from "./atributos/AtributosFormModal.vue";
import AtributosBillingSequenceSelectorModal from "./atributos/AtributosBillingSequenceSelectorModal.vue";

export default {
  name: "Atributos",
  components: {
    AtributosNivelesCard,
    AtributosJerarquiaModal,
    AtributosBillingSequenceModal,
    AtributosFormModal,
    AtributosBillingSequenceSelectorModal,
  },
  props: {
    propProducto: {
      type: [Number, String],
      default: null,
    },
    propOperador: {
      type: [Number, String],
      default: null,
    },
    propServicio: {
      type: [Number, String],
      default: null,
    },
    propTipoSuscripcion: {
      type: [Number, String],
      default: null,
    },
    propIntegrador: {
      type: [Number, String],
      default: null,
    },
    isModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      servicios: [],
      productos: [],
      operadores: [],
      tipoSuscripciones: [],
      success: null,
      integradorSel: localStorage.getItem("integrador"),
      idIntegradorProp: null,
      error: null,
      atributos: null,
      jerarquia: null,
      atributosBusqueda: null,
      atributoSel: "",
      todos: 1,
      idKeywordSel: null,
      idProductoSel: null,
      idOperadorSel: null,
      idTipoSuscripcionSel: null,
      post: false,

      // Modales
      showJerarquiaModal: false,
      showBillingSequenceModal: false,
      showBillingSequenceSelectorModal: false,
      billingSequenceSelectorAtributo: null,
      showFormModal: false,
      selectedBillingSequence: null,
      modalLoading: false,

      editData: null,
      operation: null,
      page: 1,
      errors: null,
      borraNivel: null,
      valor: null,
      nivelpiso: "default",
      busquedaTimeout: null,
      filtroAtributoNombre: "",
    };
  },
  created() {
    this.initParams();
    this.getAtributos();
    this.getAtributosBusqueda();
  },
  watch: {
    propProducto(val) {
      this.idProductoSel = val;
      this.debounceBusqueda();
    },
    propOperador(val) {
      this.idOperadorSel = val;
      this.debounceBusqueda();
    },
    propServicio(val) {
      this.idKeywordSel = val;
      this.debounceBusqueda();
    },
    propTipoSuscripcion(val) {
      this.idTipoSuscripcionSel = val;
      this.debounceBusqueda();
    },
    propIntegrador(val) {
      this.idIntegradorProp = val;
    },
    "$route.query": {
      handler() {
        if (!this.isModal) {
          this.initParams();
          this.debounceBusqueda();
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters("auth", ["user"]),
    ...mapGetters("integrador", ["integradores"]),

    userRoleLabel() {
      if (!this.user) return "";
      const rol = this.user.rol || "";
      if (rol.toLowerCase() === "admin") return "Administrador";
      if (rol.toLowerCase() === "publica") return "Integrador";
      if (rol.toLowerCase() === "crm") return "CRM";
      return rol ? rol.charAt(0).toUpperCase() + rol.slice(1) : "";
    },

    effectiveIntegradorId() {
      const integId =
        this.idIntegradorProp !== null &&
        this.idIntegradorProp !== undefined &&
        this.idIntegradorProp !== "" &&
        this.idIntegradorProp !== "todos"
          ? this.idIntegradorProp
          : (this.propIntegrador !== null &&
             this.propIntegrador !== undefined &&
             this.propIntegrador !== "" &&
             this.propIntegrador !== "todos"
               ? this.propIntegrador
               : null);
      if (integId) {
        return integId;
      }
      const globalInteg =
        this.$store.state.integrador.integrador || localStorage.getItem("integrador");
      if (globalInteg && globalInteg !== "todos") {
        return globalInteg;
      }
      return null;
    },

    integradorNombre() {
      return this.getIntegradorName(this.effectiveIntegradorId);
    },

    computedNivelTitle() {
      if (this.idKeywordSel) return `Servicio (ID: ${this.idKeywordSel})`;
      if (this.idProductoSel) return `Producto (ID: ${this.idProductoSel})`;
      if (this.idOperadorSel) return `Operador (ID: ${this.idOperadorSel})`;
      if (this.idTipoSuscripcionSel) return `Tipo De Suscripción (ID: ${this.idTipoSuscripcionSel})`;
      return "Default";
    },

    atributosFiltrados() {
      if (!this.atributosBusqueda || !this.atributosBusqueda.data) return [];
      let list = this.atributosBusqueda.data;

      if (!this.filtroAtributoNombre || !this.filtroAtributoNombre.trim()) {
        return list;
      }
      const term = this.filtroAtributoNombre.trim().toLowerCase();
      return list.filter((item) => {
        return item.atributo && item.atributo.toLowerCase().includes(term);
      });
    },
  },
  methods: {
    getDescripcionAtributo(item) {
      if (!item) return "";
      if (item.descripcion) return String(item.descripcion).trim();
      const nombre = item.atributo || "";
      if (this.atributos && Array.isArray(this.atributos)) {
        const found = this.atributos.find(
          (a) =>
            (a.id_atributo && item.id_atributo && a.id_atributo === item.id_atributo) ||
            (a.atributo && String(a.atributo).toLowerCase() === String(nombre).toLowerCase())
        );
        if (found && found.descripcion) {
          return String(found.descripcion).trim();
        }
      }
      return "";
    },

    getTooltipAtributo(item) {
      if (!item) return "";
      const desc = this.getDescripcionAtributo(item);
      if (desc) {
        return `${desc}`;
      }
      return `Sin descripción`;
    },

    isValorVacio(val) {
      return val === null || val === undefined || String(val).trim() === "";
    },

    getExplicacionNivel(nivel) {
      switch (nivel) {
        case "servicio":
          return "Nivel Servicio: mayor prioridad, específico para este servicio.";
        case "producto":
          return "Nivel Producto: aplica al producto y sus servicios.";
        case "operador":
          return "Nivel Operador: configuración propia de la telco/operador.";
        case "tipo_suscripcion":
          return "Nivel Tipo de Suscripción: modalidad de la suscripción.";
        case "default":
          return "Nivel Default: valor base global del catálogo del sistema.";
        default:
          return nivel;
      }
    },

    initParams() {
      const q = (this.$route && this.$route.query) || {};
      if (this.propIntegrador !== null && this.propIntegrador !== undefined) {
        this.idIntegradorProp = this.propIntegrador;
      } else if (q.id_integrador && q.id_integrador > 0) {
        this.idIntegradorProp = q.id_integrador;
      } else if (q.propIntegrador && q.propIntegrador > 0) {
        this.idIntegradorProp = q.propIntegrador;
      }

      if (this.propProducto !== null && this.propProducto !== undefined) {
        this.idProductoSel = this.propProducto;
      } else if (q.propProducto && q.propProducto > 0) {
        this.idProductoSel = q.propProducto;
      }

      if (this.propOperador !== null && this.propOperador !== undefined) {
        this.idOperadorSel = this.propOperador;
      } else if (q.propOperador && q.propOperador > 0) {
        this.idOperadorSel = q.propOperador;
      }

      if (this.propTipoSuscripcion !== null && this.propTipoSuscripcion !== undefined) {
        this.idTipoSuscripcionSel = this.propTipoSuscripcion;
      } else if (q.propTipoSuscripcion && q.propTipoSuscripcion > 0) {
        this.idTipoSuscripcionSel = q.propTipoSuscripcion;
      }

      if (this.propServicio !== null && this.propServicio !== undefined) {
        this.idKeywordSel = this.propServicio;
      } else if (q.propServicio && q.propServicio > 0) {
        this.idKeywordSel = q.propServicio;
      }
    },

    getIntegradorName(id) {
      if (!id) return "";
      const list =
        (this.integradores && this.integradores.data && this.integradores.data.length && this.integradores.data) ||
        (this.user && this.user.integradores && Array.isArray(this.user.integradores) && this.user.integradores) ||
        [];
      const found = list.find((i) => (i.id_integrador || i.id) == id);
      return found ? found.integrador : String(id);
    },

    openBillingSequenceSelector(atributo, op = "edit") {
      this.errors = null;
      this.editData = atributo;
      this.valor = atributo ? atributo.valor : null;
      this.operation = op;
      this.billingSequenceSelectorAtributo = atributo;
      this.showBillingSequenceSelectorModal = true;
    },

    onBillingSequenceSelected(selectedId) {
      this.onSaveAtributo(selectedId);
      this.showBillingSequenceSelectorModal = false;
      this.showFormModal = false;
    },

    getNivelPiso() {
      if (this.idKeywordSel != null) {
        this.nivelpiso = "servicio";
      } else if (this.idProductoSel != null) {
        this.nivelpiso = "producto";
      } else if (this.idOperadorSel != null) {
        this.nivelpiso = "operador";
      } else if (this.idTipoSuscripcionSel != null) {
        this.nivelpiso = "tipo_suscripcion";
      } else {
        this.nivelpiso = "default";
      }
    },

    getAtributos() {
      this.atributos = null;
      return fetchCachedCatalog(process.env.VUE_APP_API_URL + "atributo/list")
        .then((response) => {
          this.atributos = response.data;
        })
        .catch((e) => {
          this.errors = e;
        });
    },

    getBillingSequencesBusqueda(id) {
      if (!id || String(id).trim() === "") return;
      this.$store.commit("setLoading", true);
      return axios
        .post(process.env.VUE_APP_API_URL + "billingsequence/busqueda", {
          id_billing_sequence: id,
          get_all: true,
        })
        .then((response) => {
          let found = null;
          if (Array.isArray(response.data) && response.data.length > 0) {
            found = response.data[0];
          } else if (response.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
            found = response.data.data[0];
          } else if (response.data && (response.data.id_billing_sequence || response.data.sequence)) {
            found = response.data;
          }
          if (found) {
            this.selectedBillingSequence = found;
          } else {
            this.selectedBillingSequence = { id_billing_sequence: id, id: id };
          }
          this.showBillingSequenceModal = true;
        })
        .catch((e) => {
          if (e.response && e.response.status == 429) {
            Swal.fire({
              title: "Error!",
              text: "Espere un momento y vuelva a intentarlo.",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          } else {
            this.selectedBillingSequence = { id_billing_sequence: id, id: id };
            this.showBillingSequenceModal = true;
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
        });
    },

    getAtributosBusqueda() {
      this.atributosBusqueda = null;
      this.post = false;
      this.$store.commit("setLoading", true);
      const payload = {
        atributo: this.atributoSel,
        todos: this.todos,
        id_servicio: this.idKeywordSel,
        id_keyword: this.idKeywordSel,
        id_producto: this.idProductoSel,
        id_operador: this.idOperadorSel,
        id_tipo_suscripcion: this.idTipoSuscripcionSel,
      };
      return axios
        .post(process.env.VUE_APP_API_URL + "atributo/busqueda", payload)
        .then((response) => {
          this.atributosBusqueda = response.data;
          this.getNivelPiso();
        })
        .catch((e) => {
          this.errors = e;
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
          this.post = true;
        });
    },

    showDelete(atributo, nivel) {
      this.errors = null;
      this.editData = atributo;
      this.borraNivel = nivel;
      this.operation = "delete";
      this.showFormModal = true;
    },

    deleteAtributo() {
      this.errors = null;
      this.modalLoading = true;
      this.$store.commit("setLoading", true);
      axios
        .post(process.env.VUE_APP_API_URL + "atributo/delete", {
          atributo: this.editData.atributo,
          id_servicio: this.editData.id_servicio || this.editData.id_keyword_has_producto || this.idKeywordSel,
          id_keyword: this.editData.id_servicio || this.editData.id_keyword_has_producto || this.idKeywordSel,
          id_producto: this.editData.id_producto,
          id_operador: this.editData.id_operador,
          id_tipo_suscripcion: this.editData.id_tipo_suscripcion,
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            this.errors = response.data.error;
          } else {
            Swal.fire({
              title: "¡Eliminado!",
              text: "Atributo eliminado correctamente a este nivel",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
            this.showFormModal = false;
            this.operation = null;
            this.editData = null;
            this.getAtributosBusqueda();
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && e.response.data.error) {
            this.errors = e.response.data.error;
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar eliminar",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.modalLoading = false;
          this.$store.commit("setLoading", false);
        });
    },

    onSaveAtributo(nuevoValor) {
      this.errors = null;
      this.modalLoading = true;
      this.$store.commit("setLoading", true);
      axios
        .post(process.env.VUE_APP_API_URL + "atributo/update", {
          atributo: this.editData.atributo,
          id_servicio: this.idKeywordSel,
          id_keyword: this.idKeywordSel,
          id_producto: this.idProductoSel,
          id_operador: this.idOperadorSel,
          id_tipo_suscripcion: this.idTipoSuscripcionSel,
          valor: nuevoValor,
        })
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.errno &&
            response.data.errno > 0
          ) {
            this.errors = response.data.error;
          } else {
            Swal.fire({
              title: "¡Bien!",
              text: "Atributo actualizado correctamente",
              icon: "success",
              timer: 1500,
              showConfirmButton: false,
            });
            this.showFormModal = false;
            this.operation = null;
            this.editData = null;
            this.getAtributosBusqueda();
          }
        })
        .catch((e) => {
          if (e.response && e.response.data && e.response.data.error) {
            this.errors = e.response.data.error;
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al intentar guardar",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.modalLoading = false;
          this.$store.commit("setLoading", false);
        });
    },

    showEdit(atributo) {
      if (atributo && atributo.atributo === "billing_sequence_id") {
        this.openBillingSequenceSelector(atributo, "edit");
        return;
      }
      this.errors = null;
      this.editData = atributo;
      this.valor = atributo.valor;
      this.operation = "edit";
      this.showFormModal = true;
    },

    showCreate(atributo) {
      if (atributo && atributo.atributo === "billing_sequence_id") {
        this.openBillingSequenceSelector(atributo, "create");
        return;
      }
      this.errors = null;
      this.valor = atributo.valor;
      this.editData = atributo;
      this.operation = "create";
      this.showFormModal = true;
    },

    showJerarquia(atributo) {
      this.jerarquia = null;
      this.$store.commit("setLoading", true);
      this.editData = atributo;
      axios
        .post(process.env.VUE_APP_API_URL + "atributo/jerarquia", {
          atributo: atributo.atributo,
          id_servicio: this.idKeywordSel,
          id_keyword: this.idKeywordSel,
          id_producto: this.idProductoSel,
          id_operador: this.idOperadorSel,
          id_tipo_suscripcion: this.idTipoSuscripcionSel,
        })
        .then((response) => {
          this.jerarquia =
            response.data && response.data.data
              ? response.data.data
              : response.data;
          this.showJerarquiaModal = true;
        })
        .catch((e) => {
          if (e.response && e.response.data && e.response.data.error) {
            Swal.fire({
              title: "Error!",
              text: typeof e.response.data.error === "string" ? e.response.data.error : "Error al acceder a la jerarquía",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          } else {
            Swal.fire({
              title: "Error!",
              text: "Ha ocurrido un error al acceder a la jerarquía",
              icon: "error",
              confirmButtonText: "Aceptar",
            });
          }
        })
        .finally(() => {
          this.$store.commit("setLoading", false);
        });
    },

    closeJerarquiaModal() {
      this.showJerarquiaModal = false;
    },

    closeFormModal() {
      this.showFormModal = false;
      this.operation = null;
    },

    debounceBusqueda() {
      if (this.busquedaTimeout) {
        clearTimeout(this.busquedaTimeout);
      }
      this.busquedaTimeout = setTimeout(() => {
        this.getAtributosBusqueda();
      }, 50);
    },
  },
};
</script>

<style scoped>
.fila-vacia,
.table-striped tbody tr.fila-vacia:nth-of-type(odd),
.table-striped tbody tr.fila-vacia:nth-of-type(even),
.fila-vacia > td,
.fila-vacia > th {
  background-color: #f8d7da !important;
  color: #721c24 !important;
}

.fila-vacia:hover,
.table-striped tbody tr.fila-vacia:hover,
.fila-vacia:hover > td,
.fila-vacia:hover > th {
  background-color: #f5c6cb !important;
}

.table-fixed {
  table-layout: fixed;
  width: 100%;
}

.col-atributo {
  width: 35%;
  min-width: 140px;
}

.col-nivel {
  width: 130px;
  min-width: 105px;
}

.col-valor {
  width: auto;
  min-width: 120px;
}

.col-acciones {
  width: 120px;
  min-width: 105px;
}

@media (max-width: 992px) {
  .col-atributo {
    width: 33%;
    min-width: 125px;
  }
}

@media (max-width: 768px) {
  .col-atributo {
    width: 30%;
    min-width: 110px;
  }
  .col-nivel {
    width: 110px;
    min-width: 95px;
  }
  .col-acciones {
    width: 110px;
    min-width: 95px;
  }
}

@media (max-width: 576px) {
  .col-atributo {
    width: 28%;
    min-width: 95px;
  }
  .col-nivel {
    width: 95px;
    min-width: 85px;
  }
  .col-acciones {
    width: 95px;
    min-width: 85px;
  }
}

.table th {
  padding: 4px 6px !important;
  vertical-align: middle !important;
}

.table td {
  padding: 3px 6px !important;
  vertical-align: middle !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table td > span {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.badge-default-hierarchy {
  background-color: #ffffff !important;
  color: #000000 !important;
  border: 1px solid #000000 !important;
}
</style>
